import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/sonner';

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-950">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
  </div>
);

const Landing = lazy(() => import('@/pages/LandingNew'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Login = lazy(() => import('@/pages/Login'));
const Templates = lazy(() => import('@/pages/Templates'));
const Dashboard = lazy(() => import('@/pages/DashboardNew'));
const Editor = lazy(() => import('@/pages/EditorNew'));
const BrandKit = lazy(() => import('@/pages/BrandKit'));
const BookingSettings = lazy(() => import('@/pages/BookingSettings'));
const BookingHub = lazy(() => import('@/pages/BookingHub'));
const PublicBooking = lazy(() => import('@/pages/PublicBooking'));
const AdminSettings = lazy(() => import('@/pages/AdminSettings'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const SiteBuilder = lazy(() => import('@/pages/SiteBuilder'));
const PublicSite = lazy(() => import('@/pages/PublicSite'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const Banners = lazy(() => import('@/pages/Banners'));
const Affiliate = lazy(() => import('@/pages/Affiliate'));
const AdminDesign = lazy(() => import('@/pages/AdminDesign'));
const AdminSupport = lazy(() => import('@/pages/AdminSupport'));
const AdminLeads = lazy(() => import('@/pages/AdminLeads'));
const Hub = lazy(() => import('@/pages/Hub'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const Coach = lazy(() => import('@/pages/Coach'));
const AvocatLanding = lazy(() => import('@/pages/AvocatLanding'));
const BillingSuccess = lazy(() => import('@/pages/BillingSuccess'));
const SSOCallback = lazy(() => import('@/pages/SSOCallback'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/avocat" element={<AvocatLanding />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/sso/callback" element={<SSOCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/editor" element={<ProtectedRoute><Editor /></ProtectedRoute>} />
        <Route path="/editor/:id" element={<ProtectedRoute><Editor /></ProtectedRoute>} />
        <Route path="/brand-kit" element={<ProtectedRoute><BrandKit /></ProtectedRoute>} />
        <Route path="/booking" element={<ProtectedRoute><BookingHub /></ProtectedRoute>} />
        <Route path="/site-builder" element={<ProtectedRoute><SiteBuilder /></ProtectedRoute>} />
        <Route path="/site-builder/:id" element={<ProtectedRoute><SiteBuilder /></ProtectedRoute>} />
        <Route path="/booking/settings" element={<ProtectedRoute><BookingSettings /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/banners" element={<ProtectedRoute><Banners /></ProtectedRoute>} />
        <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
        
        <Route path="/rdv/:slug" element={<PublicBooking />} />
        <Route path="/book/:slug" element={<PublicBooking />} />
        <Route path="/s/:slug" element={<PublicSite />} />

        <Route path="/hub" element={<ProtectedRoute><Hub /></ProtectedRoute>} />
        <Route path="/coach" element={<ProtectedRoute><Coach /></ProtectedRoute>} />

        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/design" element={<AdminDesign />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/admin/leads" element={<AdminLeads />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
